import { useEffect, useRef } from 'react';
import { AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { useConfirmStore } from './useConfirm';

const VARIANT_CONFIG = {
  danger: {
    icon: AlertTriangle,
    iconClass: 'text-danger',
    iconBg: 'bg-danger-light',
    confirmClass: 'bg-danger hover:bg-rose-700 focus-visible:ring-danger',
  },
  warning: {
    icon: AlertCircle,
    iconClass: 'text-warning',
    iconBg: 'bg-warning-light',
    confirmClass: 'bg-warning hover:bg-amber-600 focus-visible:ring-warning',
  },
  info: {
    icon: Info,
    iconClass: 'text-primary-muted',
    iconBg: 'bg-primary-light',
    confirmClass: 'bg-primary hover:bg-primary-hover focus-visible:ring-primary',
  },
};

export function ConfirmDialog() {
  const { isOpen, options, resolve, _close } = useConfirmStore();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const variant = options?.variant ?? 'danger';
  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;

  const handleConfirm = () => {
    resolve?.(true);
    _close();
  };

  const handleCancel = () => {
    resolve?.(false);
    _close();
  };

  // Focus the cancel button when dialog opens (safer default)
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => cancelRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // ESC key
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel();
      if (e.key === 'Tab') {
        // Simple focus trap between cancel and confirm
        const focusable = [cancelRef.current, confirmRef.current].filter(Boolean) as HTMLButtonElement[];
        const idx = focusable.indexOf(document.activeElement as HTMLButtonElement);
        e.preventDefault();
        focusable[(idx + (e.shiftKey ? -1 : 1) + focusable.length) % focusable.length]?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  if (!isOpen || !options) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-[fadeIn_120ms_ease-out]"
        onClick={handleCancel}
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-surface rounded-2xl shadow-xl
                      animate-[slideUp_180ms_cubic-bezier(0.16,1,0.3,1)]">
        {/* Close button */}
        <button
          onClick={handleCancel}
          className="absolute top-4 right-4 p-1 text-content-subtle hover:text-content-muted transition-colors rounded-lg hover:bg-surface-overlay"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6">
          {/* Icon + Title */}
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${config.iconBg} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${config.iconClass}`} />
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <h2
                id="confirm-title"
                className="text-base font-semibold text-content leading-snug"
              >
                {options.title}
              </h2>
              {options.message && (
                <p className="mt-1.5 text-sm text-content-subtle leading-relaxed">
                  {options.message}
                </p>
              )}
              {options.detail && (
                <p className="mt-1 text-xs text-content-subtle leading-relaxed">
                  {options.detail}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3 justify-end">
            <button
              ref={cancelRef}
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-content-muted bg-surface-overlay
                         hover:bg-border rounded-lg transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border"
            >
              {options.labelCancel ?? 'Annuler'}
            </button>
            <button
              ref={confirmRef}
              onClick={handleConfirm}
              className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                         ${config.confirmClass}`}
            >
              {options.labelConfirm ?? 'Confirmer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
